import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Grid,
  Divider,
  IconButton,
  Tooltip,
  Dialog,
  DialogContent,
  Button,
  CircularProgress,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import FromHomeTab from "./FromHomeTab";
import FromStoreTab from "./FromStoreTab";
import {
  getAppConfigRequest,
  getStoreListForUserRequest,
} from "../../redux/Dashboard/dashboardSlice";
import { useAppTheme } from "../../theme/useAppTheme";
import useCurrency from "../../hooks/useCurrency";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { toast } from "react-toastify";
import {
  resetJewelleryOfflineOrderState,
  verifyJewelleryOfflineOrderRequest,
} from "../../redux/Jewellery/jewellerySlice";

const OfflinePaymentScreen = () => {
  const { state, pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isJewelleryOffline = pathname === "/artificialJewelleryOfflinePayment";
  const verifyStarted = useRef(false);
  const verifiedStoreRef = useRef(null);
  const paymentDetailsRef = useRef(null);

  const { storeList, appConfig } = useSelector(
    (state) => state.dashboard || {},
  );
  const { offlineVerifying, offlineVerified, offlineVerifyError } = useSelector(
    (state) => state.jewellery || {},
  );

  const offlineOrderData = state?.offlineOrderData;

  const offlinePaymentDetails =
    state?.appConfig?.offlinePaymentDetails ?? appConfig?.offlinePaymentDetails;

  const offlinePaymentType = appConfig?.offlinePaymentType;
  const configReady = Boolean(appConfig);
  const showHome = configReady
    ? offlinePaymentType
      ? Boolean(offlinePaymentType.home)
      : true
    : false;
  const showStore = isJewelleryOffline
    ? true
    : configReady
      ? offlinePaymentType
        ? Boolean(offlinePaymentType.store)
        : true
      : false;

  useEffect(() => {
    if (!isJewelleryOffline) return;
    dispatch(resetJewelleryOfflineOrderState());
  }, [isJewelleryOffline, dispatch]);

  useEffect(() => {
    dispatch(getAppConfigRequest());
    dispatch(
      getStoreListForUserRequest({
        id: `${process.env.REACT_APP_JEWELLER_ID}`,
      }),
    );
  }, [dispatch]);

  const themeColors = useAppTheme();

  const { symbol } = useCurrency();

  const [tab, setTab] = useState(null);
  const [openQr, setOpenQr] = useState(false);
  const [storeVerifying, setStoreVerifying] = useState(false);
  const [storeVerifyError, setStoreVerifyError] = useState("");
  const orderIds = offlineOrderData?.orderIds?.length
    ? offlineOrderData.orderIds
    : offlineOrderData?.orderId
      ? [offlineOrderData.orderId]
      : [];

  useEffect(() => {
    if (!configReady) return;
    setTab((current) => {
      if (current === "home" && showHome) return current;
      if (current === "store" && showStore) return current;
      if (isJewelleryOffline && showStore) return "store";
      if (showHome) return "home";
      if (showStore) return "store";
      return null;
    });
  }, [configReady, showHome, showStore, isJewelleryOffline]);

  const handleJewelleryStoreVerified = async (store) => {
    const branchCode = store?.branchCode;
    if (!branchCode || !orderIds.length || offlineVerifying || storeVerifying) return;
    verifiedStoreRef.current = store;
    if (orderIds.length === 1) {
      verifyStarted.current = true;
      dispatch(
        verifyJewelleryOfflineOrderRequest({
          orderId: orderIds[0],
          branchCode,
        }),
      );
      return;
    }

    setStoreVerifying(true);
    setStoreVerifyError("");
    try {
      for (const orderId of orderIds) {
        const response = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/api/v1/artificial-jewellery/verifyOfflineOrder`,
          { orderId, branchCode },
        );
        if (!response.data?.success) {
          throw new Error(response.data?.message || "Could not verify the offline order");
        }
      }
      navigate("/payment-status", {
        state: {
          isSuccess: true,
          orderId: orderIds.join(", "),
          metalName: "Jewellery",
          amount: offlineOrderData.finalAmount,
          successMessage:
            "Your jewellery order is verified. No metal balance is added.",
        },
      });
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error.message ||
        "Could not verify the offline order";
      setStoreVerifyError(message);
      toast.error(message);
    } finally {
      setStoreVerifying(false);
    }
  };

  useEffect(() => {
    if (!isJewelleryOffline || !verifyStarted.current || !offlineVerified) return;
    verifyStarted.current = false;
    navigate("/payment-status", {
      state: {
        isSuccess: true,
        orderId: offlineOrderData.orderId,
        metalName: "Jewellery",
        amount: offlineOrderData.finalAmount,
        successMessage:
          "Your jewellery order is verified. No metal balance is added.",
      },
    });
  }, [isJewelleryOffline, offlineVerified, offlineOrderData, navigate]);

  useEffect(() => {
    if (!isJewelleryOffline || tab !== "home" || !offlinePaymentDetails) return;
    paymentDetailsRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, [isJewelleryOffline, tab, offlinePaymentDetails]);

  const handleCopy = async (value) => {
    try {
      await navigator.clipboard.writeText(String(value));
      toast.success("Copied!");
    } catch (err) {
      toast.error("Failed to copy");
    }
  };

  if (!offlineOrderData) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6" color="error">
          Offline order data not found!
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        background: themeColors.scaffoldGradientColors,
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        p: 4,
      }}
    >
      <Card
        sx={{
          width: "100%",
          maxWidth: 900,
          borderRadius: 3,
          boxShadow: 5,
          p: 3,
          background: themeColors.bigContainer,
          border: `1px solid ${themeColors.scaffold}`,
          color: themeColors.text,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: themeColors.primary,
            fontFamily: "Aeonik",
            mb: 2,
            textAlign: "center",
          }}
        >
          Offline Payment
        </Typography>

        <Card
          sx={{
            borderRadius: 3,
            mb: 3,
            boxShadow: 3,
            background: themeColors.bigContainer,
            border: `1px solid ${themeColors.scaffold}`,
            color: themeColors.text,
          }}
        >
          <CardContent>
            <Typography
              variant="h6"
              sx={{ mb: 2, fontFamily: "Aeonik", fontWeight: 600 }}
            >
              Order Details
            </Typography>

            {/* Order ID row */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 2,
                py: 1,
                mb: 2,
                borderRadius: 3,
                background: themeColors.mediumContainer,
                border: `1px solid ${themeColors.scaffold}`,
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  width: { xs: 90, sm: 110 },
                  flexShrink: 0,
                }}
              >
                Order ID :
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 700,
                  flex: 1,
                  minWidth: 0,
                  wordBreak: "break-all",
                }}
              >
                {orderIds.length > 1 ? orderIds.join(", ") : offlineOrderData.orderId}
              </Typography>
              <Tooltip title="Copy">
                <IconButton
                  size="small"
                  onClick={() =>
                    handleCopy(
                      orderIds.length > 1 ? orderIds.join(", ") : offlineOrderData.orderId,
                    )
                  }
                  sx={{ color: themeColors.text }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Stat tiles */}
            <Grid container spacing={2}>
              {(isJewelleryOffline
                ? [
                    ["Piece", offlineOrderData.jewelleryName || "Jewellery"],
                    ["Quantity", offlineOrderData.quantity || 1],
                    ["Metal", "Jewellery"],
                  ]
                : [
                    [
                      "Metal",
                      `${offlineOrderData.metalName} - ${offlineOrderData.karat}k - ${offlineOrderData.purity}`,
                    ],
                    ["Weight", `${offlineOrderData.weight} gms`],
                    ["Tax (3%)", `${symbol}${offlineOrderData.tax}`],
                  ]
              ).map(([label, value]) => (
                <Grid size={{ xs: 12, sm: 4 }} key={label}>
                  <Box
                    sx={{
                      height: "100%",
                      px: 2,
                      py: 1.5,
                      borderRadius: 3,
                      textAlign: "center",
                      background: themeColors.mediumContainer,
                      border: `1px solid ${themeColors.scaffold}`,
                    }}
                  >
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      {label}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      {value}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            {/* Total amount  */}
            <Divider sx={{ my: 2,opacity: 0.3  }} />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 1,
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                Total Amount
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>
                {symbol}
                {offlineOrderData.finalAmount}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {tab === "home" && showHome && offlinePaymentDetails && (
          <Card
            ref={isJewelleryOffline ? paymentDetailsRef : undefined}
            sx={{
              borderRadius: 3,
              mb: 3,
              boxShadow: 3,
              background: themeColors.bigContainer,
              border: `1px solid ${themeColors.scaffold}`,
              color: themeColors.text,
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                sx={{ mb: 2, fontFamily: "Aeonik", fontWeight: 600 }}
              >
                Payment Details
              </Typography>

              <Grid container spacing={2} alignItems="stretch">
                {/* LEFT: detail rows */}
                <Grid
                  size={{
                    xs: 12,
                    md: offlinePaymentDetails.qrCodeUrl ? 8 : 12,
                  }}
                >
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                  >
                    {[
                      ["Bank Name", offlinePaymentDetails.bankName],
                      ["Account No", offlinePaymentDetails.accountNo],
                      ["IFSC Code", offlinePaymentDetails.ifscCode],
                      ["UPI ID", offlinePaymentDetails.upiId],
                    ]
                      .filter(([, value]) => value)
                      .map(([label, value]) => (
                        <Box
                          key={label}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            px: 2,
                            py: 1,
                            borderRadius: 3,
                            background: themeColors.mediumContainer,
                            border: `1px solid ${themeColors.scaffold}`,
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 600,
                              width: { xs: 90, sm: 110 },
                              flexShrink: 0,
                            }}
                          >
                            {label} :
                          </Typography>

                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 700,
                              flex: 1,
                              minWidth: 0,
                              wordBreak: "break-all",
                            }}
                          >
                            {value}
                          </Typography>

                          <Tooltip title="Copy">
                            <IconButton
                              size="small"
                              onClick={() => handleCopy(value)}
                              sx={{ color: themeColors.text }}
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ))}
                  </Box>
                </Grid>

                {/* RIGHT: QR panel */}
                {offlinePaymentDetails.qrCodeUrl && (
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Box
                      onClick={() => setOpenQr(true)}
                      sx={{
                        height: "100%",
                        minHeight: 160,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                        p: 2,
                        borderRadius: 3,
                        cursor: "pointer",
                        textAlign: "center",
                        border: `2px dashed ${themeColors.scaffold}`,
                        background: themeColors.mediumContainer,
                        transition: "transform 0.2s",
                        "&:hover": { transform: "translateY(-3px)" },
                      }}
                    >
                      <QrCode2Icon
                        sx={{ fontSize: 80, color: themeColors.text }}
                      />
                      <Typography sx={{ fontWeight: 700 }}>
                        Scan to Pay
                      </Typography>
                      <Button
                        size="small"
                        variant="contained"
                        sx={{
                          borderRadius: "20px",
                          textTransform: "none",
                          background: themeColors.primary,
                          color: themeColors.text,
                          border: `1px solid ${themeColors.scaffold}`,
                          "&:hover": { background: themeColors.primary },
                        }}
                      >
                        View QR
                      </Button>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        )}

        {(showHome || showStore) && tab && (
          <Tabs
            value={tab}
            onChange={(_, value) => setTab(value)}
            textColor="primary"
            indicatorColor="primary"
            variant="fullWidth"
            sx={{ mb: 2 }}
          >
            {showHome && (
              <Tab
                value="home"
                label="From Home"
                sx={{ fontWeight: tab === "home" ? 600 : 500 }}
              />
            )}
            {showStore && (
              <Tab
                value="store"
                label="From Store"
                sx={{ fontWeight: tab === "store" ? 600 : 500 }}
              />
            )}
          </Tabs>
        )}

        {(showHome || showStore) && <Divider sx={{ mb: 2 }} />}

        <Card
          sx={{
            borderRadius: 3,
            p: 2,
            boxShadow: 3,
            background: themeColors.bigContainer,
            border: `1px solid ${themeColors.scaffold}`,
            color: themeColors.text,
          }}
        >
          {tab === "home" && showHome ? (
            <FromHomeTab
              orderID={offlineOrderData.orderId}
              orderIds={isJewelleryOffline ? orderIds : undefined}
              uploadPath={
                isJewelleryOffline
                  ? "/api/v1/artificial-jewellery/updatePaymentDetailsForJewellery"
                  : undefined
              }
            />
          ) : tab === "store" && showStore ? (
            <>
              <FromStoreTab
                storeList={storeList}
                offlineOrderData={offlineOrderData}
                orderType={isJewelleryOffline ? "PURCHASE" : "BUY"}
                onVerified={
                  isJewelleryOffline ? handleJewelleryStoreVerified : undefined
                }
              />
              {isJewelleryOffline && (offlineVerifying || storeVerifying) && (
                <Box sx={{ mt: 2, textAlign: "center" }}>
                  <CircularProgress size={28} sx={{ color: themeColors.primary }} />
                  <Typography sx={{ mt: 1 }}>Verifying order...</Typography>
                </Box>
              )}
              {isJewelleryOffline &&
                (offlineVerifyError || storeVerifyError) &&
                !offlineVerifying &&
                !storeVerifying && (
                <Button
                  fullWidth
                  variant="contained"
                  sx={{ mt: 2, textTransform: "none" }}
                  onClick={() =>
                    handleJewelleryStoreVerified(verifiedStoreRef.current)
                  }
                >
                  Retry verification
                </Button>
              )}
            </>
          ) : (
            <Typography sx={{ textAlign: "center", py: 2 }}>
              {configReady
                ? "Offline payment is not available."
                : "Loading payment options..."}
            </Typography>
          )}
        </Card>
      </Card>
      <Dialog
        open={openQr}
        onClose={() => setOpenQr(false)}
        maxWidth="xs"
        fullWidth
      >
        <IconButton
          onClick={() => setOpenQr(false)}
          sx={{ position: "absolute", top: 8, right: 8 }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ textAlign: "center", pt: 6 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Scan to Pay
          </Typography>
          <Box
            component="img"
            src={offlinePaymentDetails?.qrCodeUrl}
            alt="Payment QR Code"
            sx={{ width: "100%", maxWidth: 300, borderRadius: 2 }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default OfflinePaymentScreen;
